import { useMemo } from 'react';
import { Box, Typography, Avatar, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useThemeMode, type ThemeName } from '../../../context/ThemeContext';

export interface OrgMember {
  id: string;
  name: string;
  rolle: string | null;
  funktion?: string | null;
  email?: string | null;
}

interface Props {
  members: OrgMember[];
}

// Reihenfolge oben -> unten aus Projektmanager-Sicht: Auftrag/Governance oben
// (wer beauftragt, finanziert, steuert), dann die Projektleitung, dann das Team.
// Externe und Fachexperten gehoeren NICHT zur eigenen Firma (farblich abgesetzt).
const INTERNAL_TIERS = [
  'Sponsor',
  'Auftraggeber',
  'Lenkungskreis',
  'Projektleiter',
  'Teilprojektleiter',
  'Teammitglied',
] as const;

const EXTERNAL_TIERS = ['Fachexperte', 'Extern'] as const;
const EXTERNAL_SET = new Set<string>(EXTERNAL_TIERS);
const SONSTIGE = 'Sonstige';

// Layout-Konstanten (deterministisch berechnet, keine Verschiebung).
const NODE_W = 188;
const NODE_H = 60;
const GAP_X = 28;
const ROW_GAP = 56;
const PAD = 18;
const ROW_H = NODE_H + ROW_GAP;

function initialsOf(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';
}

interface PlacedNode {
  member: OrgMember;
  external: boolean;
  cx: number; // Mittelpunkt X
  top: number; // obere Kante Y
}

/**
 * Projekt-Organigramm: zentriertes, deterministisch berechnetes Baum-Layout.
 * Y-Achse nach Rolle (Ebene), X-Achse nach Reihenfolge des Hinzufuegens. Jede
 * Person ist per Verbindungslinie (n:m) mit ALLEN Personen der direkt
 * angrenzenden Ebenen verbunden. Externe/Fachexperten sind farblich abgesetzt.
 */
export function TeamOrgCanvas({ members }: Props) {
  const { t } = useTranslation('projects');

  const { tiers, hasExternal } = useMemo(() => {
    const byTier = new Map<string, OrgMember[]>();
    for (const raw of members) {
      // Altwert „Externer“ heißt jetzt „Extern“ (PHQ-100).
      const m = raw.rolle === 'Externer' ? { ...raw, rolle: 'Extern' } : raw;
      const role = m.rolle ?? '';
      const tier = EXTERNAL_SET.has(role) ? role
        : (INTERNAL_TIERS as readonly string[]).includes(role) ? role
        : SONSTIGE;
      const list = byTier.get(tier) ?? [];
      list.push(m);
      byTier.set(tier, list);
    }
    const order = [...INTERNAL_TIERS, SONSTIGE, ...EXTERNAL_TIERS];
    const occupied = order
      .map((tier) => ({ tier, members: byTier.get(tier) ?? [], external: EXTERNAL_SET.has(tier) }))
      .filter((row) => row.members.length > 0);
    return { tiers: occupied, hasExternal: occupied.some((r) => r.external) };
  }, [members]);

  const { theme } = useThemeMode();
  const themed = theme === 'football' || theme === 'spacetrek' || theme === 'unicorn' || theme === 'terminal' || theme === 'matrix';

  const { nodes, lines, width, height } = useMemo(() => {
    const tierWidth = (k: number) => k * NODE_W + Math.max(0, k - 1) * GAP_X;
    const treeWidth = Math.max(NODE_W, ...tiers.map((row) => tierWidth(row.members.length)));
    const treeHeight = PAD * 2 + tiers.length * NODE_H + Math.max(0, tiers.length - 1) * ROW_GAP;
    // Bei Theme-Hintergruenden (Fussballfeld/Sternenkarte) mehr Flaeche geben und
    // den Baum darin zentrieren, damit das Motiv Platz hat.
    const canvasWidth = Math.max(themed ? 480 : NODE_W, treeWidth);
    const canvasHeight = themed ? Math.max(treeHeight, 260) : treeHeight;
    const offsetY = (canvasHeight - treeHeight) / 2;

    // Knoten je Ebene zentriert platzieren.
    const placedByTier: PlacedNode[][] = tiers.map((row, ti) => {
      const tw = tierWidth(row.members.length);
      const startX = (canvasWidth - tw) / 2;
      const top = PAD + ti * ROW_H + offsetY;
      return row.members.map((member, i) => ({
        member,
        external: row.external,
        cx: startX + i * (NODE_W + GAP_X) + NODE_W / 2,
        top,
      }));
    });

    // n:m-Verbindungen zwischen jeweils benachbarten Ebenen.
    const connectors: { id: string; d: string; external: boolean }[] = [];
    for (let ti = 0; ti < placedByTier.length - 1; ti++) {
      for (const a of placedByTier[ti]) {
        for (const b of placedByTier[ti + 1]) {
          const y1 = a.top + NODE_H;
          const y2 = b.top;
          const mid = (y1 + y2) / 2;
          connectors.push({
            id: `${a.member.id}__${b.member.id}`,
            d: `M ${a.cx} ${y1} C ${a.cx} ${mid}, ${b.cx} ${mid}, ${b.cx} ${y2}`,
            external: a.external || b.external,
          });
        }
      }
    }

    return { nodes: placedByTier.flat(), lines: connectors, width: canvasWidth, height: canvasHeight };
  }, [tiers, themed]);

  return (
    <Box className="pm-scroll" sx={{ overflowX: 'auto', overflowY: 'hidden', py: 1 }}>
      <Box sx={{ position: 'relative', width, height, mx: 'auto' }}>
        <OrgBackground theme={theme} width={width} height={height} />
        <svg width={width} height={height} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {lines.map((l) => (
            <path
              key={l.id}
              d={l.d}
              fill="none"
              stroke={l.external ? 'var(--pm-warning)' : (theme === 'unicorn' ? '#c89bf0' : 'var(--rittec-petrol)')}
              strokeOpacity={theme === 'unicorn' ? 0.9 : 0.35}
              strokeWidth={1.5}
            />
          ))}
        </svg>
        {nodes.map((n) => (
          <Box
            key={n.member.id}
            sx={{ position: 'absolute', left: n.cx - NODE_W / 2, top: n.top, width: NODE_W, height: NODE_H }}
          >
            <MemberCard member={n.member} external={n.external} theme={theme} />
          </Box>
        ))}
      </Box>

      {hasExternal && (
        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: 'var(--pm-warning)' }}>
          {t('team.orgExternal', 'Extern · nicht zur eigenen Firma')}
        </Typography>
      )}
    </Box>
  );
}

function MemberCard({ member, external, theme }: { member: OrgMember; external: boolean; theme: ThemeName }) {
  const secondary = member.funktion
    ? (member.rolle ? `${member.funktion} · ${member.rolle}` : member.funktion)
    : (member.rolle ?? '');

  // Unicorn: Mitglieder als Woelkchen.
  if (theme === 'unicorn') return <CloudCard name={member.name} secondary={secondary} external={external} />;

  const accent = external ? 'var(--pm-warning)' : 'var(--rittec-petrol)';
  return (
    <Box
      sx={{
        width: '100%', height: '100%', px: 1.25, borderRadius: 1, display: 'flex', alignItems: 'center',
        background: external ? 'var(--pm-bg-muted)' : 'var(--pm-bg-deep)',
        color: 'var(--pm-fg-base)',
        ...(external
          ? { border: `2px dashed ${accent}` }
          : { border: '1px solid var(--pm-bg-muted)', borderLeft: `4px solid ${accent}` }),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, width: '100%' }}>
        <Avatar sx={{ width: 30, height: 30, bgcolor: accent, color: external ? '#1d252d' : '#fff', fontSize: '0.72rem', fontWeight: 700 }}>
          {initialsOf(member.name)}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600, lineHeight: 1.2 }}>{member.name}</Typography>
          {secondary && (
            <Typography variant="caption" noWrap sx={{ display: 'block', lineHeight: 1.2, color: external ? 'var(--pm-warning)' : 'var(--pm-fg-muted)' }}>
              {secondary}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

/**
 * Theme-abhaengiger Hintergrund des Organigramms: Fussball -> stilisiertes
 * Spielfeld, Space Trek -> Sternenkarte. Andere Themes: kein Hintergrund.
 */
function OrgBackground({ theme, width, height }: { theme: ThemeName; width: number; height: number }) {
  if (theme === 'football') return <FootballPitch width={width} height={height} />;
  if (theme === 'spacetrek') return <StarMap width={width} height={height} />;
  if (theme === 'unicorn') return <UnicornSky width={width} height={height} />;
  if (theme === 'terminal') return <TerminalGrid width={width} height={height} />;
  if (theme === 'matrix') return <TerminalGrid width={width} height={height} color="#00ff41" bg="#000300" />;
  return null;
}

const RAINBOW = ['#ff595e', '#ff924c', '#ffca3a', '#8ac926', '#1982c4', '#4267ac', '#6a4c93'];

function UnicornSky({ width, height }: { width: number; height: number }) {
  const cx = width / 2;
  const cy = height * 0.62; // Bogen woelbt sich nach oben
  const band = 8;
  const outerR = Math.max(width, height) * 0.55;
  return (
    <svg width={width} height={height} style={bgStyle} aria-hidden>
      <defs>
        <linearGradient id="pm-org-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#bfe3ff" />
          <stop offset="100%" stopColor="#e9f6ff" />
        </linearGradient>
      </defs>
      <rect x={0} y={0} width={width} height={height} fill="url(#pm-org-sky)" />
      <g fill="none" strokeWidth={band} strokeOpacity={0.55}>
        {RAINBOW.map((color, i) => {
          const r = outerR - i * band;
          return (
            <path key={color} d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} stroke={color} />
          );
        })}
      </g>
    </svg>
  );
}

const bgStyle: React.CSSProperties = { position: 'absolute', inset: 0, pointerEvents: 'none', borderRadius: 6 };

function FootballPitch({ width, height }: { width: number; height: number }) {
  const m = 10;
  const stripeH = 38;
  const stripes = Math.ceil(height / stripeH);
  const cx = width / 2;
  const cy = height / 2;
  const boxW = Math.min(width * 0.55, 260);
  const boxH = 44;
  const circleR = Math.max(28, Math.min(width, height) * 0.13);
  return (
    <svg width={width} height={height} style={{ ...bgStyle, opacity: 0.5 }} aria-hidden>
      {Array.from({ length: stripes }).map((_, i) => (
        <rect key={i} x={0} y={i * stripeH} width={width} height={stripeH} fill={i % 2 ? '#2f7d33' : '#37893d'} />
      ))}
      <g stroke="#ffffff" strokeOpacity={0.65} strokeWidth={2} fill="none">
        <rect x={m} y={m} width={width - 2 * m} height={height - 2 * m} rx={4} />
        <line x1={m} y1={cy} x2={width - m} y2={cy} />
        <circle cx={cx} cy={cy} r={circleR} />
        <rect x={cx - boxW / 2} y={m} width={boxW} height={boxH} />
        <rect x={cx - boxW / 2} y={height - m - boxH} width={boxW} height={boxH} />
      </g>
      <circle cx={cx} cy={cy} r={2.5} fill="#ffffff" fillOpacity={0.7} />
    </svg>
  );
}

function StarMap({ width, height }: { width: number; height: number }) {
  const stars = useMemo(() => {
    // Deterministischer Pseudo-Zufall (LCG), damit die Sterne stabil bleiben.
    let seed = 9973;
    const rnd = () => {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      return seed / 0x7fffffff;
    };
    const count = Math.max(60, Math.round((width * height) / 2400));
    return Array.from({ length: count }, () => ({
      x: rnd() * width,
      y: rnd() * height,
      r: 0.4 + rnd() * 1.4,
      o: 0.2 + rnd() * 0.7,
    }));
  }, [width, height]);

  return (
    <svg width={width} height={height} style={bgStyle} aria-hidden>
      <defs>
        <radialGradient id="pm-org-nebula" cx="50%" cy="38%" r="75%">
          <stop offset="0%" stopColor="#13234a" />
          <stop offset="100%" stopColor="#05070f" />
        </radialGradient>
      </defs>
      <rect x={0} y={0} width={width} height={height} fill="url(#pm-org-nebula)" />
      {stars.map((s, i) => (
        <circle key={i} cx={s.x} cy={s.y} r={s.r} fill="#dfe9ff" fillOpacity={s.o} />
      ))}
    </svg>
  );
}

/** Terminal-Hintergrund: dezentes Monospace-Gitter mit verstreuten ASCII-Glyphen. */
function TerminalGrid({ width, height, color = '#e6e6e6', bg = '#0a0a0a' }: { width: number; height: number; color?: string; bg?: string }) {
  const glyphs = useMemo(() => {
    // Deterministischer Pseudo-Zufall (LCG), damit das Muster stabil bleibt.
    let seed = 1337;
    const rnd = () => {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      return seed / 0x7fffffff;
    };
    const chars = ['0', '1', '>', '$', '#', '/', '*', '+', '.', ';', ':', '_'];
    const count = Math.max(24, Math.round((width * height) / 9000));
    return Array.from({ length: count }, () => ({
      x: rnd() * width,
      y: rnd() * height,
      c: chars[Math.floor(rnd() * chars.length)] ?? '0',
      o: 0.05 + rnd() * 0.12,
    }));
  }, [width, height]);

  const GRID = 24;
  return (
    <svg width={width} height={height} style={bgStyle} aria-hidden>
      <defs>
        <pattern id="pm-org-termgrid" width={GRID} height={GRID} patternUnits="userSpaceOnUse">
          <path d={`M ${GRID} 0 L 0 0 0 ${GRID}`} fill="none" stroke={color} strokeOpacity={0.05} strokeWidth={1} />
        </pattern>
      </defs>
      <rect x={0} y={0} width={width} height={height} fill={bg} />
      <rect x={0} y={0} width={width} height={height} fill="url(#pm-org-termgrid)" />
      {glyphs.map((g, i) => (
        <text key={i} x={g.x} y={g.y} fill={color} fillOpacity={g.o} fontFamily="monospace" fontSize={12}>
          {g.c}
        </text>
      ))}
    </svg>
  );
}

/** Unicorn-Variante eines Mitglieds: ein Woelkchen mit zentriertem Text. */
function CloudCard({ name, secondary, external }: { name: string; secondary: string; external: boolean }) {
  const fill = external ? '#fde8cf' : '#ffffff';
  const nameColor = external ? '#9a6a1f' : '#5b3f86';
  const subColor = external ? '#b07d2e' : '#8a72b5';
  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <svg width={NODE_W} height={NODE_H} viewBox={`0 0 ${NODE_W} ${NODE_H}`} style={{ position: 'absolute', inset: 0 }} aria-hidden>
        <g fill={fill}>
          <ellipse cx={NODE_W * 0.5} cy={NODE_H * 0.66} rx={NODE_W * 0.46} ry={NODE_H * 0.30} />
          <circle cx={NODE_W * 0.30} cy={NODE_H * 0.54} r={NODE_H * 0.30} />
          <circle cx={NODE_W * 0.50} cy={NODE_H * 0.42} r={NODE_H * 0.40} />
          <circle cx={NODE_W * 0.70} cy={NODE_H * 0.52} r={NODE_H * 0.32} />
        </g>
      </svg>
      <Box sx={{ position: 'relative', height: '100%', px: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 700, lineHeight: 1.15, color: nameColor }}>{name}</Typography>
        {secondary && (
          <Typography variant="caption" noWrap sx={{ lineHeight: 1.15, color: subColor }}>{secondary}</Typography>
        )}
      </Box>
    </Box>
  );
}
